import React from 'react';
import {
  Table,
  Button,
  MoreActions,
  Badge,
  Spinner,
} from '../../../../../components';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import UnpublishedIcon from '@mui/icons-material/Unpublished';
import { DeleteOutline, Edit, Download } from '@mui/icons-material';
import type {
  KnowledgeSourceItem,
  ActionType,
  ExtendedActionCardPayload,
  TableCellData,
} from '../types';
import {
  LABELS,
  TABLE_CONFIG,
  SOURCE_STATUS,
  SORT_FIELDS,
  DATE_FORMAT_OPTIONS,
  ACTION_TYPES,
} from '../constants';
import {
  getSourceStatus,
  showActivateButton,
  createSourceActions,
} from '../utils';
import { statusConfig } from '../../utils/const';

type SourceTableProps = {
  data: KnowledgeSourceItem[];
  loading: boolean;
  sort: any;
  onSort: (sort: any) => void;
  onActivateSource: (
    id: string,
    vectorized?: boolean,
    fileType?: string
  ) => void;
  onSourceAction: (
    action: ActionType,
    payload: ExtendedActionCardPayload
  ) => void;
};

export const SourceTable: React.FC<SourceTableProps> = ({
  data,
  loading,
  sort,
  onSort,
  onActivateSource,
  onSourceAction,
}) => {
  const getTableColumns = () => [
    {
      header: '',
      cellClassName: 'min-w-[2rem] max-w-[2rem]',
      getValue: ({ status }: TableCellData) => (
        <div className="truncate">
          {getSourceStatus(status) === SOURCE_STATUS.IN_PROGRESS ? (
            <Spinner className="h-10 w-10" />
          ) : (
            ''
          )}
        </div>
      ),
    },
    {
      header: LABELS.NAME,
      sortName: SORT_FIELDS.NAME,
      cellClassName: 'min-w-[5rem] max-w-[25rem]',
      getValue: ({ name }: TableCellData) => (
        <div className="truncate">{name}</div>
      ),
    },
    {
      header: LABELS.STATUS,
      sortName: SORT_FIELDS.STATUS,
      cellClassName: 'min-w-[5rem] text-left',
      getValue: ({ active, status }: TableCellData) => (
        <Badge status={getSourceStatus(status, active)} config={statusConfig} />
      ),
    },
    {
      header: LABELS.FILE_SIZE,
      sortName: SORT_FIELDS.FILE_SIZE,
      cellClassName: 'min-w-[5rem] text-center',
      getValue: ({ file_size }: TableCellData) => <p>{file_size}</p>,
    },
    {
      header: LABELS.CREATED,
      sortName: SORT_FIELDS.CREATED_AT,
      cellClassName: 'min-w-[5rem] text-left',
      getValue: ({ created_at }: TableCellData) => {
        const date = new Date(created_at || '');
        return date.toLocaleDateString('en-GB', {
          day: DATE_FORMAT_OPTIONS.DAY,
          month: DATE_FORMAT_OPTIONS.MONTH,
          year: DATE_FORMAT_OPTIONS.YEAR,
        });
      },
    },
    {
      header: '',
      cellClassName: 'min-w-[5rem]',
      getValue: ({
        active,
        id,
        status,
        vectorized,
        file_type,
        name,
      }: TableCellData) => {
        const source = {
          active: active ?? false,
          status: status || '',
          id: id ?? '',
          name: name ?? '',
        };
        const actions = createSourceActions(source, {
          onRename: (id: string) =>
            onSourceAction(ACTION_TYPES.RENAME, {
              action: ACTION_TYPES.RENAME,
              id,
            }),
          onToggleActive: (id: string) =>
            onSourceAction(ACTION_TYPES.DEACTIVATE, {
              action: ACTION_TYPES.DEACTIVATE,
              id,
            }),
          onViewDetails: (id: string, name: string) =>
            onSourceAction(ACTION_TYPES.DETAILS, {
              action: ACTION_TYPES.DETAILS,
              id,
              name,
            }),
          onDownload: (id: string, name: string) =>
            onSourceAction(ACTION_TYPES.DOWNLOAD, {
              action: ACTION_TYPES.DOWNLOAD,
              id,
              name,
            }),
          onDelete: (id: string) =>
            onSourceAction(ACTION_TYPES.DELETE, {
              action: ACTION_TYPES.DELETE,
              id,
            }),
        });

        const actionsWithIcons = actions.map((action, index) => {
          const icons = [
            <Edit />,
            <UnpublishedIcon />,
            <InfoOutlined />,
            <Download />,
            <DeleteOutline />,
          ];
          return {
            ...action,
            icon: icons[index] || <DeleteOutline />,
          };
        });

        return (
          <div className="flex items-center justify-end h-full min-h-[3rem]">
            {showActivateButton(active ?? false, status) && (
              <Button
                size="small"
                type="contrast"
                onClick={() =>
                  onActivateSource(id ?? '', vectorized, file_type)
                }
                label={LABELS.ACTIVATE}
              />
            )}
            <MoreActions options={actionsWithIcons} />
          </div>
        );
      },
    },
  ];

  return (
    <div className="overflow-x-auto">
      <Table
        sort={sort}
        onSort={onSort}
        data={data}
        loading={loading}
        emptyMessage={TABLE_CONFIG.EMPTY_MESSAGE}
        config={{
          columns: getTableColumns(),
        }}
      />
    </div>
  );
};
